import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Button, Typography, Space } from 'antd';
import {
    GlobalOutlined, TeamOutlined, DashboardOutlined, HeartOutlined,
    UserAddOutlined, FormOutlined, CheckCircleOutlined, RocketOutlined,
    FlagOutlined
} from '@ant-design/icons';
import './AmbassadorPage.css';

const { Title, Paragraph, Text } = Typography;

const AmbassadorPage = () => {
    const navigate = useNavigate();

    const benefits = [
        {
            icon: <TeamOutlined />,
            title: 'Gestion Affiliés',
            description: 'Créez et gérez vos affiliés pour développer votre réseau'
        },
        {
            icon: <GlobalOutlined />,
            title: 'Développez votre Réseau',
            description: 'Etendez votre influence et recrutez des chauffeurs via vos affiliés'
        },
        {
            icon: <DashboardOutlined />,
            title: 'Tableau de Bord',
            description: 'Suivez les performances de votre réseau en temps réel'
        },
        {
            icon: <HeartOutlined />,
            title: 'Partenariat Privilégié',
            description: 'Bénéficiez d\'un statut privilégié auprès de TIGOON'
        }
    ];

    const steps = [
        {
            number: '1',
            title: 'Inscription',
            description: 'Remplissez le formulaire avec vos informations personnelles'
        },
        {
            number: '2',
            title: 'Validation',
            description: 'Notre équipe examine votre dossier et vous contacte'
        },
        {
            number: '3',
            title: 'Approbation',
            description: 'Une fois approuvé, recevez votre code ambassadeur unique'
        },
        {
            number: '4',
            title: 'Développement',
            description: 'Créez vos affiliés et développez votre réseau'
        }
    ];

    const faqs = [
        {
            question: 'Qui peut devenir Brand Ambassador ?',
            answer: 'Toute personne ayant un large réseau et souhaitant développer une équipe d\'affiliés pour recruter des chauffeurs sur TIGOON Connect.'
        },
        {
            question: 'Quelle est la différence avec le programme Affiliés ?',
            answer: 'Les Affiliés recrutent directement des chauffeurs. Les Ambassadeurs créent et gèrent des affiliés qui recrutent des chauffeurs pour eux.'
        },
        {
            question: 'Comment fonctionne la création d\'affiliés ?',
            answer: 'Depuis votre tableau de bord, vous pouvez créer des comptes affiliés. Chaque affilié reçoit son propre code de parrainage pour recruter des chauffeurs.'
        },
        {
            question: 'Quels sont les avantages d\'être Ambassadeur ?',
            answer: 'Vous bénéficiez d\'un partenariat privilégié avec TIGOON, d\'un tableau de bord complet pour suivre votre réseau, et de commissions sur les recrutements.'
        }
    ];

    return (
        <div className="ambassador-page">
            {/* Hero Section */}
            <section className="ambassador-hero">
                <div className="ambassador-hero-content">
                    <div className="ambassador-hero-badge">
                        <FlagOutlined /> Programme Brand Ambassador
                    </div>
                    <Title level={1}>
                        Devenez Brand Ambassador TIGOON Connect
                    </Title>
                    <Paragraph className="ambassador-hero-subtitle">
                        Devenez Brand Ambassador et partagez votre passion pour TIGOON Connect
                        avec votre communauté. Créez et gérez vos affiliés, développez votre réseau,
                        et bénéficiez d'un partenariat privilégié avec TIGOON.
                    </Paragraph>
                    <Space size="large" wrap>
                        <Button
                            type="primary"
                            size="large"
                            icon={<UserAddOutlined />}
                            onClick={() => navigate('/ambassadeurs/inscription')}
                        >
                            Devenir Ambassadeur
                        </Button>
                        <Button
                            size="large"
                            onClick={() => navigate('/ambassadeurs/connexion')}
                        >
                            Se connecter
                        </Button>
                    </Space>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="benefits-section">
                <div className="section-container">
                    <Title level={2} className="section-title">
                        Avantages du Programme
                    </Title>
                    <Paragraph className="section-subtitle">
                        En tant qu'Ambassadeur, vous beneficiez d'avantages exclusifs
                    </Paragraph>
                    <Row gutter={[24, 24]}>
                        {benefits.map((benefit, index) => (
                            <Col xs={24} sm={12} md={6} key={index}>
                                <Card hoverable className="benefit-card">
                                    <div className="benefit-icon">{benefit.icon}</div>
                                    <Title level={4}>{benefit.title}</Title>
                                    <Paragraph>{benefit.description}</Paragraph>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>
            </section>

            {/* How it works */}
            <section className="how-it-works-section">
                <div className="section-container">
                    <Title level={2} className="section-title">
                        Comment ca marche ?
                    </Title>
                    <Paragraph className="section-subtitle">
                        Rejoindre le programme en 4 etapes simples
                    </Paragraph>
                    <Row gutter={[24, 24]}>
                        {steps.map((step, index) => (
                            <Col xs={24} sm={12} md={6} key={index}>
                                <div className="step-card">
                                    <div className="step-number">{step.number}</div>
                                    <Title level={4}>{step.title}</Title>
                                    <Paragraph>{step.description}</Paragraph>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </div>
            </section>

            {/* Stats Section */}
            <section className="stats-section">
                <div className="section-container">
                    <Row gutter={[48, 24]} justify="center">
                        <Col xs={12} md={6}>
                            <div className="stat-item">
                                <div className="stat-value">50+</div>
                                <Text>Ambassadeurs Actifs</Text>
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <div className="stat-item">
                                <div className="stat-value">200+</div>
                                <Text>Affiliés Créés</Text>
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <div className="stat-item">
                                <div className="stat-value">1000+</div>
                                <Text>Chauffeurs Recrutés</Text>
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <div className="stat-item">
                                <div className="stat-value">24/7</div>
                                <Text>Support Dédié</Text>
                            </div>
                        </Col>
                    </Row>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="faq-section">
                <div className="section-container">
                    <Title level={2} className="section-title">
                        Questions frequentes
                    </Title>
                    <Row gutter={[24, 24]}>
                        {faqs.map((faq, index) => (
                            <Col xs={24} md={12} key={index}>
                                <Card className="faq-card">
                                    <Title level={5}>{faq.question}</Title>
                                    <Paragraph>{faq.answer}</Paragraph>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="section-container">
                    <Title level={2}>Prêt à rejoindre le programme ?</Title>
                    <Paragraph>
                        Inscrivez-vous maintenant et commencez à développer votre réseau avec TIGOON Connect
                    </Paragraph>
                    <Button
                        type="primary"
                        size="large"
                        icon={<RocketOutlined />}
                        onClick={() => navigate('/ambassadeurs/inscription')}
                    >
                        S'inscrire maintenant
                    </Button>
                </div>
            </section>
        </div>
    );
};

export default AmbassadorPage;
